import { observer } from "mobx-react-lite";
import { useContext, useEffect, useRef, useState } from "react";
import { Context } from "..";
import { IHolidays } from "../models/IHolidays";
import { Week } from "../models/IWeek";

const GeneralTable = () => {
  const { store } = useContext(Context);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [monthColspan, setMonthColspan] = useState<number[]>([]);
  const [weekTitles, setWeekTitles] = useState<Week[]>([]);
  const [showMonth, setShowMonth] = useState<boolean>(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [month, setMonth] = useState(currentMonth);
  const [monthYear, setMonthYear] = useState(currentYear);
  const [monthDays, setMonthDays] = useState<Date[]>([]);

  useEffect(() => {
    store.getGeneralTable();
  }, [store]);

  useEffect(() => {
    store.renderWeekTitle(year, setWeekTitles, setMonthColspan);
  }, [year, store]);

  useEffect(() => {
    store.renderMonthDays(monthYear, month, setMonthDays);
  }, [month, monthYear, store]);

  const renderUserWeek = (periods: IHolidays[], weeks: Week[]) => {
    return weeks.map((week, index) => {
      const { checkStatus, daysRange } = store.checkPeriodInWeek(periods, week);

      return (
        <td
          key={index}
          className={`vertical ${checkStatus ? "selected colorfy" : "border"}`}
        >
          {daysRange}
        </td>
      );
    });
  };

  const renderUserRows = () => {
    return store.generalTable.map((department: any, depIndex: number) => (
      <>
        <tr key={`dep-${department._id || department.id || depIndex}`}>
          <th colSpan={2 + weekTitles.length}>{department.title}</th>
        </tr>

        {department.users.map((user: any) => (
          <tr key={user._id}>
            <td data-user-id={user._id}>
              {user.name} {user.surname}
            </td>
            <td>{user.positionTitle || user?.position?.title || ""}</td>
            {renderUserWeek(user.holidays, weekTitles)}
          </tr>
        ))}
      </>
    ));
  };

  const nextYear = (increment: number) => {
    setYear((prev) => prev + increment);
  };

  const nextMonth = () => {
    setMonth((prevMonth) => {
      if (prevMonth === 11) {
        setMonthYear((prevYear) => prevYear + 1);
        return 0;
      }
      return prevMonth + 1;
    });
  };

  const previousMonth = () => {
    setMonth((prevMonth) => {
      if (prevMonth === 0) {
        setMonthYear((prevYear) => prevYear - 1);
        return 11;
      }
      return prevMonth - 1;
    });
  };

  const submitHandler = () => {
    store.exportToExcel(store.getExportData());
  };

  const renderUserDays = (periods: IHolidays[], days: Date[]) => {
    return days.map((day, index) => {
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);

      const currentDayStart = dayStart.getTime();
      const currentDayEnd = dayEnd.getTime();

      const inHoliday = periods.some((period) => {
        const startPeriod = new Date(period.dateFrom);
        startPeriod.setHours(0, 0, 0, 0);

        const endPeriod = new Date(period.dateUntil);
        endPeriod.setHours(23, 59, 59, 999);

        return (
          startPeriod.getTime() <= currentDayEnd &&
          currentDayStart <= endPeriod.getTime()
        );
      });

      return (
        <td
          key={index}
          className={`${inHoliday ? "selected colorfy" : "border"}`}
        >
          {inHoliday ? day.getDate() : ""}
        </td>
      );
    });
  };

  const renderUserRowsForDays = () => {
    return store.generalTable.map((department: any, depIndex: number) => (
      <>
        <tr key={`month-dep-${department._id || department.id || depIndex}`}>
          <th colSpan={2 + monthDays.length}>{department.title}</th>
        </tr>

        {department.users.map((user: any, userIndex: number) => (
          <tr className="day-table" key={user._id}>
            <td data-user-id={user._id}>
              {user.name} {user.surname}
            </td>
            <td>{user.positionTitle || user?.position?.title || ""}</td>
            {renderUserDays(user.holidays, monthDays)}
          </tr>
        ))}
      </>
    ));
  };

  return (
    <div className="container-fluid mt-5">
      <div className="d-flex">
        <h2 className="me-5 mb-3 text-primary fw-normal">
          Общий график отпусков
        </h2>
        <button onClick={submitHandler} className="btn btn-success btn-sm me-2">
          Экспорт в Excel
        </button>
      </div>

      <div className="d-flex">
        <p
          className={`${
            showMonth ? "text-secondary" : "text-primary"
          } fs-3 me-5 cursor-pointer`}
          onClick={() => setShowMonth(false)}
        >
          Год
        </p>
        <p
          className={`${
            !showMonth ? "text-secondary" : "text-primary"
          } fs-3 cursor-pointer`}
          onClick={() => setShowMonth(true)}
        >
          Месяц
        </p>
      </div>

      {!showMonth && (
        <>
          <div className="d-flex week-calendar-header mb-3 mt-3 align-items-center">
            <button className="btn btn-secondary" onClick={() => nextYear(-1)}>
              Пред
            </button>
            <h5 className="ms-4 me-4 mb-0 fw-bold fs-4 text-dark">{year}</h5>
            <button className="btn btn-secondary" onClick={() => nextYear(1)}>
              След
            </button>
          </div>

          <div className="week-calendar-container">
            <table
              className="table table-bordered my-4 table-excel"
              ref={tableRef}
            >
              <thead className="table-dark">
                <tr>
                  <th colSpan={1} rowSpan={2}>
                    Отдел/Сотрудник
                  </th>
                  <th colSpan={1} rowSpan={2}>
                    Должность
                  </th>

                  {monthColspan.map((colspan: number, index: number) => (
                    <th key={index} colSpan={colspan}>
                      {store.getMonthName(index)}
                    </th>
                  ))}
                </tr>

                <tr>
                  {weekTitles.map((week: Week, index: number) => (
                    <td
                      colSpan={1}
                      rowSpan={1}
                      key={index}
                      className="vertical"
                    >
                      {store.getDateString(week.start)} -{" "}
                      {store.getDateString(week.end)}
                    </td>
                  ))}
                </tr>
              </thead>

              <tbody>{renderUserRows()}</tbody>
            </table>
          </div>
        </>
      )}

      {showMonth && (
        <>
          <div className="d-flex align-items-center week-calendar-header mb-1 mt-5">
            <button className="btn btn-secondary" onClick={previousMonth}>
              Пред
            </button>
            <h5 className="ms-4 me-4 mb-0 fw-bold fs-4 text-dark">
              {`${store.months[month]}, ${monthYear}`}
            </h5>
            <button className="btn btn-secondary" onClick={nextMonth}>
              След
            </button>
          </div>

          <div className="week-calendar-container">
            <table className="table table-bordered my-4 table-excel">
              <thead className="table-dark">
                <tr>
                  <th colSpan={1} rowSpan={2}>
                    Отдел/Сотрудник
                  </th>
                  <th colSpan={1} rowSpan={2}>
                    Должность
                  </th>
                  <th colSpan={monthDays.length} rowSpan={1}>
                    {`${store.months[month]}, ${monthYear}`}
                  </th>
                </tr>
                <tr>
                  {monthDays.map((day: Date, index) => (
                    <th className="th-day" key={index}>
                      {day.getDate()}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>{renderUserRowsForDays()}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default observer(GeneralTable);
